<script setup lang="ts">
/**
 * Quick-add Note (E21 + E14).
 *
 * The highest-frequency collaboration action isn't creating a new record —
 * it's attaching a note to something that already exists. Nezha's daily
 * pattern is "SOC called, saw this IP on property #1742, jot a note on
 * the existing IOC and move on."
 *
 * This form lets her do that in three keystrokes: open picker (`n`),
 * select Note (`n` again), type a fuzzy search ("198.51.100"), click the
 * match, type the note, save.
 *
 * Backed by:
 *   - `GET /api/search?q=...` for fuzzy lookup across all six artifact types
 *   - `POST /api/collab/artifacts/{type}/{id}/notes` for the write
 */
import { ref, computed, onMounted, nextTick } from 'vue';
import { createNote, type CollabArtifactType } from '@/api/collab';
import { useAuthStore } from '@/stores/authStore';
import {
  useGlobalArtifactSearch,
  type GlobalArtifactHit,
} from '@/composables/useGlobalArtifactSearch';
import { Search, Plus, X, Pin } from 'lucide-vue-next';

const emit = defineEmits<{ done: [] }>();

const authStore = useAuthStore();

const orgName = computed(() => authStore.user?.orgName ?? 'your organization');
const isCwrStaff = computed(() => authStore.user?.orgSlug === 'admin');
const scopeLabel = computed(() =>
  isCwrStaff.value
    ? 'Cross-tenant — CWR staff inbox will see this'
    : `Private to ${orgName.value}`,
);

// ── Search state (SSOT: useGlobalArtifactSearch) ─────────────────

const { query, searching, searchError, flatResults } = useGlobalArtifactSearch({
  debounceMs: 200,
  minQueryLength: 2,
});

const queryInput = ref<HTMLInputElement | null>(null);

onMounted(async () => {
  await nextTick();
  queryInput.value?.focus();
});

// ── Selection + note compose ──────────────────────────────────────

const selectedHit = ref<GlobalArtifactHit | null>(null);
const noteBody = ref('');
const pinned = ref(false);
const saving = ref(false);
const saveError = ref<string | null>(null);

function pick(hit: GlobalArtifactHit) {
  selectedHit.value = hit;
  // Move focus to the textarea
  nextTick(() => {
    const el = document.querySelector<HTMLTextAreaElement>('[data-qa-field="note-body"]');
    el?.focus();
  });
}

function clearSelection() {
  selectedHit.value = null;
  noteBody.value = '';
  saveError.value = null;
  nextTick(() => queryInput.value?.focus());
}

async function save(addAnother: boolean) {
  if (!selectedHit.value || !noteBody.value.trim()) return;
  saving.value = true;
  saveError.value = null;
  try {
    await createNote(
      selectedHit.value.type,
      selectedHit.value.id,
      noteBody.value.trim(),
      pinned.value,
    );
    if (addAnother) {
      noteBody.value = '';
      pinned.value = false;
      selectedHit.value = null;
      query.value = '';
      await nextTick();
      queryInput.value?.focus();
    } else {
      emit('done');
    }
  } catch (err: unknown) {
    const resp = (err as { response?: { data?: { message?: string | string[] } } })?.response;
    const m = resp?.data?.message;
    saveError.value = Array.isArray(m) ? m[0] : (m ?? 'Save failed');
  } finally {
    saving.value = false;
  }
}

function onKeydown(e: KeyboardEvent) {
  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
    e.preventDefault();
    save(e.shiftKey);
  }
}
</script>

<template>
  <form
    class="quick-add-note-form flex flex-col gap-3"
    data-qa-form="note"
    @keydown="onKeydown"
    @submit.prevent="save(false)"
  >
    <div class="flex items-center justify-between">
      <h2 class="text-lg font-semibold text-text-primary">Add Note</h2>
      <span
        class="text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5"
        :class="isCwrStaff ? 'bg-accent-blue/15 text-accent-blue' : 'bg-surface-700 text-text-muted'"
        data-qa-scope-label
      >
        {{ scopeLabel }}
      </span>
    </div>

    <!-- No selection yet: search -->
    <template v-if="!selectedHit">
      <div class="flex flex-col gap-1">
        <label class="text-xs text-text-muted">Attach note to…</label>
        <div class="relative">
          <Search class="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-text-muted pointer-events-none" />
          <input
            ref="queryInput"
            v-model="query"
            type="text"
            placeholder="Search actors, IOCs, rules, campaigns, techniques, reports…"
            class="qa-input w-full pl-8"
            data-qa-field="note-search"
          />
        </div>
      </div>

      <div v-if="searching" class="text-xs text-text-muted py-1">Searching…</div>
      <div v-else-if="searchError" class="text-xs text-status-error">{{ searchError }}</div>

      <div
        v-if="!searching && flatResults.length > 0"
        class="quick-add-note-form-results flex flex-col gap-1 max-h-64 overflow-y-auto"
        data-qa-section="results"
      >
        <button
          v-for="hit in flatResults"
          :key="`${hit.type}-${hit.id}`"
          type="button"
          class="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-700 text-left cwr-motion"
          :data-qa-hit-type="hit.type"
          :data-qa-hit-id="hit.id"
          @click="pick(hit)"
        >
          <span class="text-[10px] uppercase tracking-wider bg-surface-900 rounded px-1.5 py-0.5 text-text-muted shrink-0">
            {{ hit.type }}
          </span>
          <span class="text-sm text-text-primary truncate flex-1">{{ hit.label }}</span>
          <span class="text-xs text-text-muted truncate">{{ hit.sublabel }}</span>
        </button>
      </div>
      <div
        v-else-if="query.length >= 2 && !searching && flatResults.length === 0"
        class="text-xs text-text-muted italic py-1"
      >
        No matches. Try a different term.
      </div>
    </template>

    <!-- Selection made: compose -->
    <template v-else>
      <div class="flex items-center justify-between bg-surface-900 rounded px-2 py-1.5">
        <div class="flex items-center gap-2 min-w-0">
          <span class="text-[10px] uppercase tracking-wider bg-surface-800 rounded px-1.5 py-0.5 text-accent-blue shrink-0">
            {{ selectedHit.type }}
          </span>
          <span class="text-sm text-text-primary truncate" data-qa-selected-label>
            {{ selectedHit.label }}
          </span>
        </div>
        <button
          type="button"
          class="text-text-muted hover:text-text-secondary cwr-motion"
          data-qa-action="clear-selection"
          @click="clearSelection"
        >
          <X class="w-3.5 h-3.5" />
        </button>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-xs text-text-muted">Note</label>
        <textarea
          v-model="noteBody"
          rows="3"
          placeholder="What did you observe?"
          class="qa-input font-normal"
          data-qa-field="note-body"
        />
      </div>

      <label class="flex items-center gap-1.5 text-xs text-text-muted">
        <input
          v-model="pinned"
          type="checkbox"
          class="rounded border-border-default"
          data-qa-field="note-pin"
        />
        <Pin class="w-3 h-3" />
        Pin to top
      </label>

      <div v-if="saveError" class="text-xs text-status-error">{{ saveError }}</div>

      <div class="flex items-center justify-between pt-1">
        <p class="text-[10px] text-text-disabled">
          <kbd class="font-mono">ctrl+enter</kbd> save ·
          <kbd class="font-mono">ctrl+shift+enter</kbd> save and add another
        </p>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="qa-btn-secondary"
            :disabled="!noteBody.trim() || saving"
            data-qa-action="save-and-add"
            @click="save(true)"
          >
            <Plus class="w-3 h-3" />
            Save & add another
          </button>
          <button
            type="submit"
            class="qa-btn-primary"
            :disabled="!noteBody.trim() || saving"
            data-qa-action="save"
          >
            {{ saving ? 'Saving…' : 'Save note' }}
          </button>
        </div>
      </div>
    </template>
  </form>
</template>

<style scoped>
.qa-input {
  background: var(--color-surface-700, #2a2a3e);
  border: 1px solid var(--color-border-default, #3a3a4e);
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  color: var(--color-text-primary, #e5e5ea);
  font-size: 0.875rem;
  outline: none;
}
.qa-input:focus { border-color: var(--color-accent-blue, #3b82f6); }

.qa-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: var(--color-accent-blue, #3b82f6);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}
.qa-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

.qa-btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: var(--color-surface-700, #2a2a3e);
  color: var(--color-text-secondary, #a0a0aa);
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  border: 1px solid var(--color-border-default, #3a3a4e);
}
.qa-btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
